import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../../../shared/api-client/index.js';
import {
  entityDetailKey,
  entityListKey,
  type EntityListKey,
} from '../../../shared/api/entityQueryKeys.js';

export type ProjectStatus = 'planned' | 'active' | 'on_hold' | 'completed' | 'cancelled';

export interface ProjectMemberRef {
  id: string;
  userId: string;
  user: { id: string; name: string; email: string };
}

export interface ProjectMemberEntry {
  id: string;
  role: string;
  member: ProjectMemberRef;
}

export interface ProjectItem {
  id: string;
  name: string;
  description?: string | null;
  status: ProjectStatus;
  ownerMember?: ProjectMemberRef | null;
  startDate?: string | null;
  dueDate?: string | null;
  createdAt: string;
  updatedAt: string;
  taskCount: number;
  meetingCount: number;
  noteCount: number;
  members: ProjectMemberEntry[];
}

export interface ListProjectsFilters {
  q?: string;
  status?: ProjectStatus;
  ownerMemberId?: string;
  limit?: number;
}

export interface CreateProjectInput {
  name: string;
  description?: string | null;
  status?: ProjectStatus;
  ownerMemberId?: string | null;
  startDate?: string | null;
  dueDate?: string | null;
  memberIds?: string[];
}

export interface UpdateProjectInput {
  name?: string;
  description?: string | null;
  status?: ProjectStatus;
  ownerMemberId?: string | null;
  startDate?: string | null;
  dueDate?: string | null;
}

export function getProjectsQueryKey(
  workspaceId: string,
  filters: ListProjectsFilters = {},
): EntityListKey {
  return entityListKey('projects', workspaceId, { ...filters });
}

export function getProjectDetailQueryKey(
  workspaceId: string,
  projectId: string,
): readonly string[] {
  return entityDetailKey('projects', workspaceId, projectId);
}

export async function listProjects(filters: ListProjectsFilters = {}): Promise<ProjectItem[]> {
  const search = new URLSearchParams();
  if (filters.q?.trim()) search.set('q', filters.q.trim());
  if (filters.status) search.set('status', filters.status);
  if (filters.ownerMemberId) search.set('ownerMemberId', filters.ownerMemberId);
  if (filters.limit !== undefined) search.set('limit', String(filters.limit));
  const path = search.size > 0 ? `/projects?${search.toString()}` : '/projects';
  return apiGet<ProjectItem[]>(path);
}

export async function getProjectById(projectId: string): Promise<ProjectItem> {
  return apiGet<ProjectItem>(`/projects/${projectId}`);
}

export async function createProject(input: CreateProjectInput): Promise<ProjectItem> {
  return apiPost<ProjectItem, CreateProjectInput>('/projects', input);
}

export async function updateProject(id: string, input: UpdateProjectInput): Promise<ProjectItem> {
  return apiPatch<ProjectItem, UpdateProjectInput>(`/projects/${id}`, input);
}

export async function deleteProject(id: string): Promise<void> {
  await apiDelete<void>(`/projects/${id}`);
}

export async function setProjectMembers(id: string, memberIds: string[]): Promise<ProjectItem> {
  return apiPut<ProjectItem, { memberIds: string[] }>(`/projects/${id}/members`, { memberIds });
}
